import {
  BucketData,
  PaymentPeriod,
  RevenueReport,
  TaxDataModel,
  TaxPeriod,
  TaxReportLicense,
} from 'src/types/tax';
import {
  calculateChecksum,
  COLUMN_NAMES,
  TemplateErrors,
  TemplateRecord,
} from './utils';
import {
  ERROR_NUMBER,
  ERROR_MAX_VALUE,
  ERROR_MIN_VALUE,
} from 'src/components/form/constants';

interface RecordError {
  rowIndex: number;
  message: string;
}

interface RecordMetadata {
  rowIdentification?: {
    periodId: string;
    licenseId: string;
    provider: string;
  };
}

interface ValidatedRecord {
  record: TemplateRecord;
  metadata: RecordMetadata;
}

const currencyValidator = (
  value: number,
  maximum = 1000000,
  minimum = 0
): string | undefined => {
  if (isNaN(value)) return ERROR_NUMBER;
  if (value >= maximum) return ERROR_MAX_VALUE(maximum);
  if (value < minimum) return ERROR_MIN_VALUE(minimum);
};

const validateRecord = (
  record: TemplateRecord,
  includeDeductions?: boolean
): TemplateErrors => {
  const errors: TemplateErrors = {};

  const totalRevenueCurrency = currencyValidator(
    Number(record[COLUMN_NAMES.TOTAL_REVENUE])
  );
  if (totalRevenueCurrency) {
    errors[COLUMN_NAMES.TOTAL_REVENUE] = totalRevenueCurrency;
  }
  if (includeDeductions) {
    const deductionsCurrency = currencyValidator(
      Number(record[COLUMN_NAMES.ALLOWABLE_DEDUCTIONS])
    );
    if (deductionsCurrency) {
      errors[COLUMN_NAMES.ALLOWABLE_DEDUCTIONS] = deductionsCurrency;
    }
  }
  return errors;
};

export const validateRecords = async (
  records: Array<TemplateRecord>,
  periods: Array<PaymentPeriod>,
  bucketPerPeriod: Record<string, Array<BucketData>>,
  includeDeductions?: boolean
) => {
  const periodsById: Record<string, PaymentPeriod> = periods.reduce(
    (prev, period) => ({ ...prev, [period.id]: period }),
    {}
  );

  const validatedRecords: Array<ValidatedRecord> = [];

  const errors: Array<RecordError> = [];
  const allColumns = Object.values(COLUMN_NAMES).filter((colNames) =>
    includeDeductions ? true : colNames !== COLUMN_NAMES.ALLOWABLE_DEDUCTIONS
  );

  for (let rowIndex = 0; rowIndex < records.length; rowIndex++) {
    const record = records[rowIndex];

    // Check if we have any missing columns
    const missingColumns = allColumns.filter((column) => {
      const value = record[column];
      // Check if value is missing (null, undefined, empty string, or whitespace-only string)
      if (value === null || value === undefined || value === '') {
        return true;
      }
      // Check if value is a valid number (including 0) or non-empty string
      const numValue = Number(value);
      if (!isNaN(numValue)) {
        return false; // Valid number, including 0
      }
      // Check if it's a non-empty string
      return !value.toString().trim();
    });

    if (missingColumns.length) {
      errors.push({
        rowIndex,
        message: `Missing columns: ${missingColumns.join(', ')}`,
      });
      continue;
    }
    // Check if we have any errors in the expected data
    const invalidColumnErrors = Object.values(
      validateRecord(record, includeDeductions)
    );

    if (invalidColumnErrors.length > 0) {
      invalidColumnErrors.forEach(([column, error]) => {
        errors.push({
          rowIndex,
          message: `Column ${column}: ${error}`,
        });
      });
      continue;
    }

    // Check checksum for the record
    const providedChecksum = record[COLUMN_NAMES.CHECKSUM];
    const computedChecksum = await calculateChecksum(record);

    if (providedChecksum !== computedChecksum) {
      errors.push({ rowIndex, message: `Invalid checksum` });
      continue;
    }

    // Check if Row ID is valid
    const rowIdentificationString = record[COLUMN_NAMES.ROW_ID];
    let rowIdentification;
    try {
      rowIdentification = JSON.parse(atob(rowIdentificationString));
    } catch (e) {
      errors.push({
        rowIndex,
        message: `${COLUMN_NAMES.ROW_ID} field has been modified`,
      });
      continue;
    }

    const { periodId, provider, licenseId } = rowIdentification;

    // Check if the period is valid
    const period = periodsById[periodId];
    if (!period) {
      errors.push({
        rowIndex,
        message: `Invalid period ${record[COLUMN_NAMES.PERIOD]}`,
      });
      continue;
    }

    // Check if the license is valid
    const license = period.licenses.find((lic) => lic.license === licenseId);
    if (!license) {
      errors.push({
        rowIndex,
        message: `License ${record[COLUMN_NAMES.PERMIT_NUMBER]} not available in your account`,
      });
      continue;
    }

    // Check if the provider is valid
    const startDate = periodsById[periodId].startDate;
    const foundProvider = bucketPerPeriod[startDate].find(
      (bucket) => bucket.key === provider
    );

    if (!foundProvider) {
      errors.push({
        rowIndex,
        message: `Platform ${record[COLUMN_NAMES.PROVIDER]} is not available in current configuration. Please download the correct report template and upload a new report.`,
      });
      continue;
    }

    validatedRecords.push({
      record,
      metadata: { rowIdentification: { periodId, provider, licenseId } },
    });
  }

  return {
    errors,
    validatedRecords,
  };
};

export const convertRecordsToPeriods = (
  records: Array<ValidatedRecord>,
  licenses: Array<PaymentPeriod>,
  bucketPerPeriod: Record<string, BucketData[]>
): TaxDataModel => {
  // periods is the initial values of the form
  const periods = licenses.map(
    (selectedLicense): TaxPeriod => ({
      licenses: selectedLicense.licenses.map((lic): TaxReportLicense => {
        const licBuckets = bucketPerPeriod[selectedLicense.startDate];
        const buckets: Array<RevenueReport> =
          licBuckets.map((bucket: BucketData): RevenueReport => {
            const record = records.find(
              (r) =>
                r.metadata?.rowIdentification?.periodId ===
                  selectedLicense.id &&
                r.metadata?.rowIdentification?.provider === bucket.key &&
                r.metadata?.rowIdentification?.licenseId === lic.license
            );
            return {
              ...bucket,
              discounts:
                Number(record?.record[COLUMN_NAMES.ALLOWABLE_DEDUCTIONS]) || 0,
              revenue: Number(record?.record[COLUMN_NAMES.TOTAL_REVENUE]),
              provider: bucket.key,
            };
          }) || [];
        return {
          occupancy: lic.occupancy || {},
          buckets,
        };
      }),
    })
  );

  return { periods };
};
